import {
  ArrowLeftRight,
  BriefcaseBusiness,
  CalendarCheck,
  CalendarPlus,
  CarFront,
  FileText,
  HeartPulse,
  KeyRound,
  Laptop,
  MessageSquarePlus,
  PackageMinus,
  PackagePlus,
  Plane,
  Presentation,
  Receipt,
  ReceiptText,
  Route,
  Stethoscope,
  Undo2,
  UserPlus,
  UserRoundCog,
  type LucideIcon,
} from "lucide-react";
import { screenAllowed, type SearchableScreen } from "@/app/layouts/global-search";
import type { DashboardPersona } from "@/features/dashboard/lib/dashboard-persona";
import { decidesForOthers, type PermissionSet } from "@/lib/permissions/permissions";

/**
 * HIZLI İŞLEMLER — paletin «ne yapmak istiyorsun» yarısı (1 Kasım 2026, kullanıcı isteği: «role ve
 * yetkiye göre hızlı işlemler»).
 *
 * Palet boş açıldığında yalnız EKRANLARI sıralıyordu; «izin talebi aç» gibi bir iş ancak yazınca
 * geliyordu ve iki tane vardı. Artık işlemlerin tek kataloğu bu dosya: palet boşken kişinin rolüne
 * göre ilk 4–8'ini (`featuredQuickActions`), yazınca hepsini ekranlarla birlikte süzüyor.
 *
 * ÜÇ KURAL:
 *   1. İşlem FORMU AÇAR, sayfaya bırakmaz. Adres formu açan adres: `?panel=new` (sayfa bunu
 *      `useNewPanelIntent` ile okuyor), sihirbazın kendi adresi (`/fleet/assignments/new`) ya da
 *      kabuğun kendi penceresi (`command`). Tek istisna «Araç iade al»: iade bir araca bağlı ve önce
 *      araç seçilir — işlem açık zimmetleri açıyor, plakayı yazana palet doğrudan «İade al»ı veriyor
 *      (`vehicleQuickAction`, üst aramanın araç kaynağı).
 *   2. Kişinin yapamayacağı işlem HİÇ görünmez (`quickActionAllowed`): işlemin kendi anahtarı,
 *      açtığı rotanın kapısı (`routeNeeds`), işin ait olduğu menü satırı (`section`) ve modül.
 *      Satır kuralı yöneticinin daraltılmış rayıyla da tutarlı: sistem yöneticisi rayında depo
 *      bölümü yoksa paletinde «Mal kabul» da yok.
 *   3. EKRANI OLMAYAN İŞLEM BURAYA GİRMEZ. Her girdinin rotası `quick-actions.test.ts`'te
 *      aranıyor; adresi açılmayan bir işlem çiti kırar.
 *
 * YENİ İŞLEM TEK GİRDİ: `href` formu açan adres (`…?panel=new` ve sayfada `useNewPanelIntent`, ya da
 * formun kendi sayfası), `permission` işi AÇAN anahtar; kişinin kendi işiyse `needsEmployee: true` ve
 * `featuredFor: "all"` («Masraf beyanı» böyle girdi, finans F2c-1).
 *
 * PANELİM BU KATALOĞU KULLANMIYOR, VE BİLEREK. Panelim kutucukları ekrana gider ve canlı bir ipucu
 * taşır («2 talep onayda»); buradakiler fiil, form açar. Aynı satırın iki kopyası yok: ikisinde de
 * geçen tek iş «Zimmet ver», o da aynı rotayı (`/fleet/assignments/new`) açıyor.
 */

/** Kabuğun kendi pencereleri: adresi olmayan, `AppLayout`'un açtığı işler. */
export type ShellCommand = "impersonate" | "return-to-self";

interface QuickActionBase extends Omit<SearchableScreen, "href" | "under"> {
  /** Sabit ad — sınamalar ve komutlar bununla buluyor. */
  id: string;
  icon: LucideIcon;
  /**
   * Boş palette kimin ilk işleri — Panelim'in persona'sı (`dashboardPersona`). `"all"`: istihdamı
   * olan herkesin kişisel işi (izin, seyahat). `[]`: yalnız aranınca.
   */
  featuredFor: readonly DashboardPersona[] | "all";
  /**
   * İşin ait olduğu MENÜ SATIRI: satır kişinin rayında yoksa işlem önerilmez. Rota o satırın
   * kapısını miras alıyorsa (`EXTRA_ROUTES` › `under`) değer onunla aynı; sınama karşılaştırıyor.
   */
  section?: string;
  /** `permission`'la AYNI soruyla (kendisi mi, başkası mı) sorulan ek anahtarlar — işe alım iki ister. */
  alsoNeeds?: readonly string[];
  /**
   * Onay işi: anahtar karar VEREBİLMEK için soruluyor (`decidesForOthers`). Sistem yöneticisinin
   * bütün anahtarları taşıması onu her onayın sahibi yapmıyor.
   */
  decides?: boolean;
  /** Form bir şirkete bağlı (sözleşme şirkette): «Tüm şirketler» seçiliyken önerilmez. */
  needsCompany?: boolean;
  /** Komut yalnız destek oturumunda (`true`) ya da yalnız dışında (`false`). */
  whileImpersonating?: boolean;
}

export type QuickAction = QuickActionBase &
  ({ href: string; command?: never } | { command: ShellCommand; href?: never });

/**
 * Katalog. Sıra önemli: aynı persona içinde ÜSTTEKİ önce gelir. Kişisel blok en sonda, çünkü
 * herkesin listesinde rolün işinden SONRA duruyor.
 */
export const QUICK_ACTIONS: readonly QuickAction[] = [
  // ── Yönetim ──────────────────────────────────────────────────────────────────────────────
  {
    id: "invite-user",
    label: "Kullanıcı davet et",
    description: "Erişim › Kullanıcılar · e-postayla davet",
    keywords: "davet kullanıcı hesap ekle e-posta yeni hesap",
    href: "/admin/access/users?panel=invite",
    icon: UserPlus,
    section: "/admin/access",
    permission: "identity.user.invite",
    // Kullanıcılar sekmesinin kendi kapısı (`AccessSectionPage`).
    routeNeeds: ["identity.user.read"],
    featuredFor: ["admin"],
  },
  {
    id: "impersonate",
    label: "Kullanıcı değiştir",
    description: "Başka bir hesabın gözünden çalışın",
    keywords: "kullanıcı değiştir hesap adına destek oturumu rol dene",
    command: "impersonate",
    icon: UserRoundCog,
    permission: "identity.user.impersonate",
    whileImpersonating: false,
    featuredFor: ["admin"],
  },
  {
    id: "return-to-self",
    label: "Kendi hesabıma dön",
    description: "Destek oturumunu bitir",
    keywords: "dön kendi hesap çık destek oturumu bitir",
    command: "return-to-self",
    icon: Undo2,
    permission: null,
    whileImpersonating: true,
    featuredFor: [],
  },

  // ── İK ───────────────────────────────────────────────────────────────────────────────────
  {
    id: "hire",
    label: "İşe alım",
    description: "İK › Personel · yeni personel ve sözleşmesi",
    keywords: "işe alım yeni personel çalışan ekle işe giriş sözleşme",
    href: "/hr/people/employees?panel=new",
    icon: BriefcaseBusiness,
    section: "/hr/people",
    permission: "hr.employee.manage",
    alsoNeeds: ["hr.employment.manage"],
    aboutOthers: true,
    needsCompany: true,
    module: "module.hr",
    featuredFor: ["hr"],
  },
  {
    id: "leave-approvals",
    label: "İzin onayları",
    description: "Onay kutusu · onayınızı bekleyen izin talepleri",
    keywords: "izin onay onayla bekleyen talep karar ret",
    href: "/approvals?type=hr.leave_request",
    icon: CalendarCheck,
    permission: "hr.leave.approve",
    decides: true,
    routeNeeds: ["workflow.approval.read"],
    module: "module.hr",
    featuredFor: ["hr", "manager", "executive"],
  },
  {
    id: "asset-handover",
    label: "Zimmet ver",
    description: "İK › Zimmet · bilgisayar, telefon, kart",
    keywords: "zimmet ver eşya bilgisayar telefon laptop demirbaş teslim",
    href: "/hr/assets?panel=new",
    icon: Laptop,
    section: "/hr/assets",
    permission: "hr.asset.manage",
    module: "module.hr",
    featuredFor: ["hr"],
  },

  // ── Depo ─────────────────────────────────────────────────────────────────────────────────
  {
    id: "goods-receipt",
    label: "Mal kabul",
    description: "Depo › Stok belgesi · giriş",
    keywords: "mal kabul giriş belge irsaliye tedarikçi teslim al stok",
    href: "/inventory/documents/new?type=RECEIPT",
    icon: PackagePlus,
    section: "/inventory",
    permission: "inventory.document.create",
    module: "module.inventory",
    featuredFor: ["warehouse"],
  },
  {
    id: "dispatch",
    label: "Sevk belgesi",
    description: "Depo › Stok belgesi · çıkış",
    keywords: "sevk çıkış belge irsaliye gönder teslim et stok",
    href: "/inventory/documents/new?type=ISSUE",
    icon: PackageMinus,
    section: "/inventory",
    permission: "inventory.document.create",
    module: "module.inventory",
    featuredFor: ["warehouse"],
  },
  {
    id: "transfer",
    label: "Depolar arası transfer",
    description: "Depo › Stok belgesi · transfer çıkışı",
    keywords: "transfer depo aktar taşı stok belge",
    href: "/inventory/documents/new?type=TRANSFER_OUT",
    icon: ArrowLeftRight,
    section: "/inventory",
    permission: "inventory.document.create",
    module: "module.inventory",
    featuredFor: ["warehouse"],
  },

  // ── Pazarlama ve saha ────────────────────────────────────────────────────────────────────
  {
    id: "open-congress",
    label: "Kongre aç",
    description: "Kongre ve Etkinlik · kongre, fuar ya da toplantı",
    keywords: "kongre aç yeni etkinlik fuar toplantı sempozyum",
    href: "/events?panel=new",
    icon: CalendarPlus,
    section: "/events",
    permission: "events.event.manage",
    module: "module.events",
    featuredFor: ["marketing"],
  },
  {
    id: "physician-visit",
    label: "Hekim görüşmesi kaydet",
    description: "Hekim Görüşmeleri · görüşmeyi gir, onaya gitsin",
    keywords: "hekim görüşme ziyaret kaydet doktor saha mümessil",
    href: "/physician-visits?panel=new",
    icon: Stethoscope,
    section: "/physician-visits",
    needsEmployee: true,
    permission: "events.physician_visit.record",
    module: "module.events",
    featuredFor: ["field", "regional"],
  },
  {
    id: "presentation-request",
    label: "Sunum talebi",
    description: "Sunum taleplerim · bir kurumda sunum isteyin",
    keywords: "sunum talep kurum hastane eğitim iste",
    href: "/my-presentation-requests?panel=new",
    icon: Presentation,
    section: "/my-presentation-requests",
    needsEmployee: true,
    permission: "events.presentation.request",
    module: "module.events",
    featuredFor: ["field", "regional"],
  },

  // ── Filo ─────────────────────────────────────────────────────────────────────────────────
  {
    id: "vehicle-handover",
    label: "Araç teslim et",
    description: "Filo › Zimmet · teslim tutanağıyla",
    keywords: "araç teslim zimmet ver anahtar sürücü filo",
    href: "/fleet/assignments/new",
    icon: KeyRound,
    section: "/fleet/assignments",
    permission: "fleet.assignment.manage",
    module: "module.fleet",
    featuredFor: ["fleet"],
  },
  {
    id: "vehicle-return",
    label: "Araç iade al",
    description: "Açık zimmetten seçin ya da plakayı yazın",
    keywords: "araç iade al geri al teslim al zimmet filo plaka",
    href: "/fleet/assignments",
    icon: CarFront,
    section: "/fleet/assignments",
    permission: "fleet.assignment.manage",
    module: "module.fleet",
    featuredFor: ["fleet"],
  },
  {
    id: "fleet-fine",
    label: "Ceza gir",
    description: "Filo › Ceza ve Geçişler · trafik cezası",
    keywords: "ceza trafik cezası gir hgs geçiş filo",
    href: "/fleet/fines?panel=new",
    icon: Receipt,
    section: "/fleet/fines",
    permission: "fleet.fine.manage",
    module: "module.fleet",
    featuredFor: ["fleet"],
  },

  // ── Operasyon ────────────────────────────────────────────────────────────────────────────
  {
    id: "transport-job",
    label: "Sevkiyat işi aç",
    description: "Operasyon › Sevkiyat · taşıma işi",
    keywords: "sevkiyat taşıma iş nakliye araç yükleme operasyon",
    href: "/operations/transport-jobs?panel=new",
    icon: Route,
    section: "/operations/transport-jobs",
    permission: "operations.transport_job.manage",
    module: "module.operations",
    featuredFor: ["employee", "manager"],
  },

  // ── Çalışma alanı ────────────────────────────────────────────────────────────────────────
  {
    id: "new-chat",
    label: "Yeni sohbet",
    description: "Sohbet · bir kişiye ya da gruba yazın",
    keywords: "sohbet mesaj yaz yeni konuşma grup",
    href: "/chat?panel=new",
    icon: MessageSquarePlus,
    section: "/chat",
    permission: "chat.message.send",
    module: "module.chat",
    featuredFor: [],
  },

  // ── Kişisel — istihdamı olan herkesin ────────────────────────────────────────────────────
  {
    id: "leave-request",
    label: "Yeni izin talebi",
    description: "İzinlerim",
    keywords: "izin talep oluştur yeni",
    href: "/my-leave/requests?panel=new",
    icon: CalendarPlus,
    needsEmployee: true,
    permission: "hr.leave.request",
    // Açtığı sayfanın kapısı ayrı bir anahtar — gerekçe `SearchableScreen.routeNeeds`'te.
    routeNeeds: ["hr.leave.read"],
    module: "module.hr",
    featuredFor: "all",
  },
  /** «Sağlık raporlarım» yerine: rapor, izin talebinin içinde (izin-sade, 20 Ekim 2026). */
  {
    id: "sick-leave",
    label: "Raporlu izin bildir",
    description: "İzinlerim · rapor yüklenir",
    keywords: "rapor istirahat sağlık hastalık yükle belge",
    href: "/my-leave/requests?panel=new&leaveType=RAPORLU",
    icon: HeartPulse,
    needsEmployee: true,
    permission: "hr.leave.request",
    routeNeeds: ["hr.leave.read"],
    module: "module.hr",
    featuredFor: [],
  },
  {
    id: "travel-request",
    label: "Seyahat talebi",
    description: "Seyahat taleplerim · uçak, konaklama, transfer",
    keywords: "seyahat talep uçak otel konaklama transfer yolculuk bilet",
    href: "/travel-requests?panel=new",
    icon: Plane,
    needsEmployee: true,
    permission: "hr.travel_request.submit",
    featuredFor: "all",
  },
  {
    id: "document-request",
    label: "Evrak talebi",
    description: "Şirket evrakı · görev belgesi, bordro ve benzeri",
    keywords: "evrak belge talep bordro görev belgesi çalışma belgesi yazı",
    href: "/document-requests?panel=new",
    icon: FileText,
    needsEmployee: true,
    permission: "hr.document_request.submit",
    featuredFor: "all",
  },
  /**
   * MASRAF BEYANI (finans F2c-1, 2 Kasım 2026): yeni beyanın kendi sayfası var (sihirbaz gibi, `?panel=new` değil); rota
   * «Masraflarım»ın kapısını miras alıyor (`under`), bu yüzden `section` o satır.
   */
  {
    id: "expense-claim",
    label: "Masraf beyanı",
    description: "Masraflarım · fiş ya da faturayla",
    keywords: "masraf beyan harcama fiş fatura geri ödeme taksi yemek otopark",
    href: "/my-expenses/new",
    icon: ReceiptText,
    needsEmployee: true,
    permission: "finance.expense.submit",
    section: "/my-expenses",
    module: "module.finance",
    featuredFor: "all",
  },
];

/** Boş palette en çok bu kadar: daha fazlası ekranları ekranın altına iter. */
export const FEATURED_MAX = 8;
/** Kişinin bu kadar işi varsa boş palet en az bunu gösterir (rolün işi az olsa da). */
export const FEATURED_MIN = 4;

export interface QuickActionContext {
  permissions: PermissionSet;
  /** Etkin bir istihdam — kişisel işler onsuz açılamıyor (Panelim'in kutucuk kuralı). */
  hasEmployee: boolean;
  /** Kişinin rayında çizilen satırların adresleri (`AppLayout` › `visibleSections`). */
  visibleSections: ReadonlySet<string>;
  /** Kapsamda tek bir şirket seçili mi. */
  hasCompany: boolean;
  impersonating: boolean;
}

export function quickActionAllowed(action: QuickAction, context: QuickActionContext): boolean {
  const { permissions } = context;
  if (action.section !== undefined && !context.visibleSections.has(action.section)) return false;
  if (action.needsCompany === true && !context.hasCompany) return false;
  if (action.whileImpersonating !== undefined && action.whileImpersonating !== context.impersonating) {
    return false;
  }
  if (action.decides === true && action.permission !== null && !decidesForOthers(permissions, action.permission)) {
    return false;
  }
  if (action.alsoNeeds !== undefined) {
    const holds = action.aboutOthers === true ? permissions.canForOthers : permissions.can;
    if (!action.alsoNeeds.every((key) => holds(key))) return false;
  }
  return screenAllowed(action, permissions, context.hasEmployee);
}

/** Kişinin yapabildiği işlemler, katalog sırasıyla. */
export function quickActionsFor(context: QuickActionContext): QuickAction[] {
  return QUICK_ACTIONS.filter((action) => quickActionAllowed(action, context));
}

/**
 * Boş paletin listesi: önce rolün işleri, sonra herkesin kişisel işleri, en çok sekiz. Rolün ve
 * kişinin işi dörtten azsa, yapabildiği öteki işlerle dörde tamamlanır — boş palet tek satırla
 * açılmasın; yapamadığı bir işle de tamamlanmaz.
 */
export function featuredQuickActions(
  allowed: readonly QuickAction[],
  persona: DashboardPersona,
): QuickAction[] {
  const mine = allowed.filter(
    (action) => action.featuredFor !== "all" && action.featuredFor.includes(persona),
  );
  const everyone = allowed.filter((action) => action.featuredFor === "all");
  const picked = [...mine, ...everyone].slice(0, FEATURED_MAX);
  if (picked.length >= FEATURED_MIN) return picked;
  const rest = allowed.filter((action) => !picked.includes(action) && action.command === undefined);
  return [...picked, ...rest].slice(0, Math.max(picked.length, FEATURED_MIN));
}

/** Paletteki satır kimliği: adresliler `eylem:<adres>` (seçilince oraya gidilir), komutlar `eylem:@<komut>`. */
export function quickActionItemId(action: QuickAction): string {
  return action.command !== undefined ? `eylem:@${action.command}` : `eylem:${action.href}`;
}

const SHELL_COMMANDS: readonly ShellCommand[] = ["impersonate", "return-to-self"];

/** Satır bir kabuk komutuysa adı; değilse `null` (adrese gidilir). */
export function shellCommandOf(itemId: string): ShellCommand | null {
  if (!itemId.startsWith("eylem:@")) return null;
  const name = itemId.slice("eylem:@".length);
  return SHELL_COMMANDS.find((command) => command === name) ?? null;
}
